import { ReactNode } from 'react';
import { xor } from 'lodash-es';
import cn from 'classnames';
import { MdBlock, MdOutlineBedtime, MdOutlineFlightTakeoff, MdOutlineLuggage, MdOutlineWbSunny } from 'react-icons/md';
import {
  BaseViolationItem,
  HubsDataDictionary,
  ScheduleStatusMode,
  ShiftEdgeKind,
  ShiftSiblingData,
} from '@lp/features/Scheduler/types';
import { CSShiftEdgeColors, CSShiftOptionType } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/types';
import {
  csShiftTypeNames,
  hubRelatedCSShiftTypes,
  singleCSShiftTypes,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import { isDifferentHrSource, toLocalDateFromApiDateKey, toZonedISODateKey } from '@lp/features/Scheduler/utils';
import { shiftTimeGroupNames } from '@/constants/schedule.constants';
import {
  CSResource,
  CSResourcesWithShifts,
  CSShift,
  CSShiftSelectPayload,
  CSShiftType,
  SetCSShiftData,
  ShiftFixationType,
  ShiftStatus,
  CSShiftTimeGroup,
} from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';

export const getCSHubToResourceMap = (data: CSResourcesWithShifts, hubToInsertedMap: Record<string, string[]>) => {
  const hubToResourceMap: Map<string, CSResource[]> = new Map();

  Object.values(data).forEach((r) => {
    if (!r.resource) return;

    const hubsInShifts = Object.values(r.days).map((s) => s.hubId);
    const allResourceHubIds = [r.resource?.hubId, ...hubsInShifts];
    const resourceHubIds = Array.from(new Set(allResourceHubIds));

    resourceHubIds.forEach((hubId) => {
      if (!hubId) return;
      const hubResources = hubToResourceMap.get(hubId) ?? [];
      hubToResourceMap.set(hubId, [...hubResources, r.resource]);
    });
  });

  Object.entries(hubToInsertedMap).forEach(([hubId, userIds]) => {
    const hubResources = hubToResourceMap.get(hubId) ?? [];
    // Manually inserted resources should be at the end of the list
    const filteredResources = hubResources.filter((r) => !userIds.includes(r.id));
    const insertedResources = userIds.map((id) => data[id]?.resource);

    hubToResourceMap.set(hubId, [...filteredResources, ...insertedResources]);
  });

  return hubToResourceMap;
};

/**
 * Determines the temporal edge (gap/overlap) between a shift and the previous-day shift based on
 * their time groups. `A → B` starts 4h later than the previous shift ends → {@link ShiftEdgeKind.Gap};
 * `B → A` starts 4h before it ends → {@link ShiftEdgeKind.Overlap}. Same group (or a missing group on
 * either side, e.g. legacy/HR shifts) yields no edge.
 */
const getCSTimeGroupEdge = (shift: CSShift, prevShift?: CSShift): ShiftEdgeKind | undefined => {
  const current = shift.timeGroup;
  const previous = prevShift?.timeGroup;
  if (!current || !previous || current === previous) return undefined;

  return previous === CSShiftTimeGroup.GroupA ? ShiftEdgeKind.Gap : ShiftEdgeKind.Overlap;
};

/** Whether a shift is served from a hub other than the currently rendered one. */
export const isCSExternalShift = (targetHubId: string, shift?: CSShift): boolean =>
  Boolean(shift?.hubId && shift.hubId !== targetHubId && shift.shiftType !== CSShiftType.TravelDay);

export const getCSSiblingDataGetter =
  (targetHubId: string, hubsDictionary: HubsDataDictionary) =>
  (shift: CSShift, prevShift?: CSShift): ShiftSiblingData => {
    const isSingle = singleCSShiftTypes.includes(shift.shiftType);
    const sameShiftType = shift.shiftType === prevShift?.shiftType;
    const sameHub = (shift.hubId ?? null) === (prevShift?.hubId ?? null);
    const sameOrgans = xor(shift.organs, prevShift?.organs).length === 0;
    const isExternal = isCSExternalShift(targetHubId, shift);
    const prevIsExternal = isCSExternalShift(targetHubId, prevShift);

    // Show gap/overlap for external<->common boundaries, but never between two external shifts.
    const prevEdge = isExternal && prevIsExternal ? undefined : getCSTimeGroupEdge(shift, prevShift);

    if (isExternal) {
      const prevIsTravel = prevShift?.shiftType === CSShiftType.TravelDay;
      const sameExternalPrev = sameHub && !prevIsTravel;

      return {
        hasPrev: sameExternalPrev,
        isExternal,
        id: shift.hubId!,
        name: hubsDictionary[shift.hubId!]?.name ?? '[UNKNOWN]', // For removed hubs,
        prevEdge,
      };
    }

    if (isSingle || !sameShiftType || !sameHub) return { hasPrev: false, prevEdge };

    if ((shift.timeGroup ?? null) !== (prevShift?.timeGroup ?? null)) return { hasPartialPrev: true, prevEdge };

    if (shift.shiftType === CSShiftType.OnCall) {
      if (shift.isExtra !== prevShift?.isExtra || !sameOrgans) return { hasPartialPrev: true, prevEdge };
    } else if (shift.shiftType === CSShiftType.PhoneSupport) {
      if (!sameOrgans) return { hasPartialPrev: true, prevEdge };
    } else if (shift.shiftType === CSShiftType.NoCall) {
      if (shift.fixationType !== prevShift?.fixationType) return { hasPartialPrev: true, prevEdge };
    }

    if (isDifferentHrSource(shift, prevShift)) return { hasPartialPrev: true, prevEdge };

    return { hasPrev: true, prevEdge };
  };

export const getCSResourceEmptyShift = (
  resourceId: string,
  daysInterval: Date[],
  shiftsData: CSResourcesWithShifts,
  hubToResourceMap: Map<string, CSResource[]>,
) => {
  const dayKeys = daysInterval.map(toZonedISODateKey);
  const segment = shiftsData[resourceId];
  if (!segment) return null;

  const hubId = Array.from(hubToResourceMap.entries()).find(([, resources]) => {
    return resources.some((r) => r.id === resourceId);
  })?.[0];

  if (hubId) {
    for (const dayKey of dayKeys) {
      if (!segment.days[dayKey]) {
        return {
          day: toLocalDateFromApiDateKey(dayKey),
          resourceId,
          hubId,
        };
      }
    }
  }

  return null;
};

export const getCSShiftSetData = (
  shiftPayload: CSShiftSelectPayload,
  resource?: CSResource,
  _hubId?: string,
  _regionId?: string,
): SetCSShiftData => {
  const { shiftType } = shiftPayload;
  const isHubRequired = hubRelatedCSShiftTypes.includes(shiftType);
  const hubId = isHubRequired ? _hubId : undefined;
  const regionId = isHubRequired ? _regionId : undefined;
  const organs = shiftType === CSShiftType.OnCall ? (shiftPayload.organs ?? resource?.organs) : shiftPayload.organs;
  const isTravelOnCall = shiftType === CSShiftType.OnCall && resource?.hubId !== hubId;
  const isTravel = isTravelOnCall || shiftType === CSShiftType.TravelDay;

  return { ...shiftPayload, organs, isTravel, regionId, hubId };
};

export const getShiftFromShiftOption = (shiftOption?: CSShiftOptionType, organs?: OrganType[]) => {
  if (!shiftOption) return undefined;

  switch (shiftOption) {
    case CSShiftOptionType.ExtraCall:
      return { shiftType: CSShiftType.OnCall, isExtra: true, organs };
    case CSShiftOptionType.FloatingHoliday:
      return { shiftType: CSShiftType.NoCall, isExtra: true, organs };
    case CSShiftOptionType.BlockedNoCall:
      return { shiftType: CSShiftType.NoCall, isExtra: false, fixationType: ShiftFixationType.Required, organs };
    default:
      return { shiftType: CSShiftType[shiftOption], isExtra: false, organs };
  }
};

export const getShiftOptionFromShift = (shift?: CSShift) => {
  if (!shift) return undefined;
  switch (true) {
    case shift.shiftType === CSShiftType.OnCall && shift.isExtra:
      return CSShiftOptionType.ExtraCall;
    case shift.shiftType === CSShiftType.NoCall && shift.isExtra:
      return CSShiftOptionType.FloatingHoliday;
    case shift.shiftType === CSShiftType.NoCall && shift.fixationType === ShiftFixationType.Required:
      return CSShiftOptionType.BlockedNoCall;
    default:
      return CSShiftOptionType[CSShiftType[shift.shiftType]];
  }
};

const colorToken = (name: string) => `hsl(var(--heroui-${name}))`;
const colorTint = (name: string, pct: number) => `color-mix(in srgb, ${colorToken(name)} ${pct}%, white)`;
const withBgStyle = (background: string): React.CSSProperties => ({ background });

export const getCSShiftColors = (shiftData?: CSShift, isExternalHub?: boolean) => {
  const { shiftType, shiftStatus, isExtra, isPreSchedule } = shiftData ?? {};
  const isPlanning = shiftStatus === ShiftStatus.InPlanningProcess;
  const borderStyle = isPreSchedule ? 'border' : 'border border-dashed';

  if (isExternalHub) {
    return {
      bg: 'bg-base-80',
      text: 'text-base-20',
      secondaryText: 'text-base-20',
      border: isPlanning || isPreSchedule ? cn(borderStyle, 'border-base-50') : 'border-transparent',
      icon: 'text-base-20',
    };
  }

  if (isPlanning || isPreSchedule) {
    switch (shiftType) {
      case CSShiftType.OnCall:
        return {
          // bg: isExtra ? 'bg-success-30/30' : 'bg-success-40/20',
          style: withBgStyle(isExtra ? colorTint('success-30', 30) : colorTint('success-40', 20)),
          text: 'text-success-5',
          secondaryText: 'text-success-20',
          border: cn(borderStyle, 'border-success-10'),
          icon: 'text-success-40',
        };
      case CSShiftType.PhoneSupport:
        return {
          // bg: 'bg-indigo-40/20',
          style: withBgStyle(colorTint('indigo-40', 20)),
          text: 'text-indigo-5',
          secondaryText: 'text-indigo-20',
          border: cn(borderStyle, 'border-indigo-10'),
          icon: 'text-indigo-30',
        };
      case CSShiftType.Vacation:
      case CSShiftType.Sick:
        return {
          // bg: 'bg-warning-40/20',
          style: withBgStyle(colorTint('warning-40', 20)),
          text: 'text-warning-5',
          secondaryText: 'text-warning-5',
          border: 'border border-warning-20',
          icon: 'text-warning-20',
        };
      case CSShiftType.AtHub:
        return {
          // bg: 'bg-info-60/30',
          style: withBgStyle(colorTint('info-60', 30)),
          text: 'text-info-5',
          secondaryText: 'text-info-20',
          border: cn(borderStyle, 'border-info-20'),
          icon: 'text-info-40',
        };
      case CSShiftType.Unavailable:
        return {
          // bg: 'bg-danger-40/10',
          style: withBgStyle(colorTint('danger-40', 10)),
          text: 'text-danger-5',
          secondaryText: 'text-danger-20',
          border: 'border border-danger-20',
          icon: 'text-danger-30',
        };
      case CSShiftType.NoCall:
      case CSShiftType.WeekendOff:
      case CSShiftType.TravelDay:
        return {
          // bg: 'bg-base-50/50',
          style: withBgStyle(colorTint('base-50', 50)),
          text: 'text-base-10',
          secondaryText: 'text-base-20',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      case CSShiftType.OfficeDay:
        return {
          bg: 'bg-base-80',
          text: 'text-base-20',
          secondaryText: 'text-base-40',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      default:
        return {
          bg: 'bg-transparent',
          text: 'text-base-25',
          secondaryText: 'text-base-70',
          border: 'border-transparent',
          icon: 'text-base-5',
        };
    }
  }

  switch (shiftType) {
    case CSShiftType.OnCall:
      return {
        bg: isExtra ? 'bg-success-30' : 'bg-success-40',
        text: 'text-white',
        secondaryText: 'text-success-70',
        border: 'border-transparent',
        icon: 'text-success-40',
      };
    case CSShiftType.PhoneSupport:
      return {
        bg: 'bg-indigo-40',
        text: 'text-white',
        secondaryText: 'text-indigo-70',
        border: 'border-transparent',
        icon: 'text-indigo-30',
      };
    case CSShiftType.Vacation:
    case CSShiftType.Sick:
      return {
        // bg: 'bg-warning-40/20',
        style: withBgStyle(colorTint('warning-40', 20)),
        text: 'text-warning-5',
        secondaryText: 'text-warning-5',
        border: 'border-transparent',
        icon: 'text-warning-20',
      };
    case CSShiftType.AtHub:
      return {
        bg: 'bg-info-60',
        text: 'text-base-5',
        secondaryText: 'text-base-5',
        border: 'border-transparent',
        icon: 'text-info-40',
      };
    case CSShiftType.Unavailable:
      return {
        // bg: 'bg-danger-40/20',
        style: withBgStyle(colorTint('danger-40', 20)),
        text: 'text-danger-5',
        secondaryText: 'text-danger-5',
        border: 'border-transparent',
        icon: 'text-danger-30',
      };
    case CSShiftType.NoCall:
    case CSShiftType.WeekendOff:
    case CSShiftType.TravelDay:
      return {
        bg: 'bg-base-50',
        text: 'text-base-10',
        secondaryText: 'text-base-20',
        border: 'border-transparent',
        icon: 'text-base-30',
      };
    case CSShiftType.OfficeDay:
    default:
      return {
        bg: 'bg-base-80',
        text: 'text-base-20',
        secondaryText: 'text-base-70',
        border: 'border-transparent',
        icon: 'text-base-5',
      };
  }
};

/** Resolves the schedule state that drives the gap/overlap patch styling. */
export const getCSShiftEdgeVariant = (shift?: CSShift): ScheduleStatusMode => {
  if (shift?.isPreSchedule) return ScheduleStatusMode.PrePlanning;
  if (shift?.shiftStatus === ShiftStatus.InPlanningProcess) return ScheduleStatusMode.Planning;
  return ScheduleStatusMode.Published;
};

/**
 * Resolves the CSS colours for a shift's gap/overlap patch. `color` mirrors the published cell
 * background (alpha backgrounds like `bg-warning-40/20` are flattened over white to stay opaque);
 * `bg` and `border` mirror the lighter pre-schedule/planning cell background and border tokens. The
 * darker stripe shade for same-colour overlaps is derived from `color` in `CSShiftEdgePatch`.
 */
export const getCSShiftEdgeColors = (shift?: CSShift, isExternalHub?: boolean): CSShiftEdgeColors => {
  if (!shift) return { color: 'transparent', bg: 'transparent', border: 'transparent' };
  if (isExternalHub) return { color: colorToken('base-80'), bg: colorToken('base-80'), border: colorToken('base-50') };

  switch (shift.shiftType) {
    case CSShiftType.OnCall:
      return {
        color: shift.isExtra ? colorToken('success-30') : colorToken('success-40'),
        bg: shift.isExtra ? colorTint('success-30', 30) : colorTint('success-40', 20),
        border: colorToken('success-10'),
      };
    case CSShiftType.PhoneSupport:
      return { color: colorToken('indigo-40'), bg: colorTint('indigo-40', 20), border: colorToken('indigo-10') };
    case CSShiftType.Vacation:
    case CSShiftType.Sick:
      return { color: colorTint('warning-40', 20), bg: colorTint('warning-40', 20), border: colorToken('warning-20') };
    case CSShiftType.AtHub:
      return { color: colorToken('info-60'), bg: colorTint('info-60', 30), border: colorToken('info-20') };
    case CSShiftType.Unavailable:
      return { color: colorTint('danger-40', 20), bg: colorTint('danger-40', 10), border: colorToken('danger-20') };
    case CSShiftType.NoCall:
    case CSShiftType.WeekendOff:
    case CSShiftType.TravelDay:
      return { color: colorToken('base-50'), bg: colorTint('base-50', 50), border: colorToken('base-20') };
    case CSShiftType.OfficeDay:
    default:
      return { color: colorToken('base-80'), bg: colorToken('base-80'), border: colorToken('base-20') };
  }
};

const getTextWithIcon = (text: ReactNode, icon: ReactNode) => (
  <>
    {text}
    {icon}
  </>
);

const csTimeGroupIconMap: Record<CSShiftTimeGroup, ReactNode> = {
  [CSShiftTimeGroup.GroupA]: <MdOutlineWbSunny size={16} />,
  [CSShiftTimeGroup.GroupB]: <MdOutlineBedtime size={16} />,
};

export const getCSShiftTypePrimaryText = (shiftData: CSShift, showTimeGroup?: boolean) => {
  const { shiftType, isExtra, isTravel, fixationType, timeGroup } = shiftData;
  const baseText = csShiftTypeNames[shiftType] ?? null;
  const suffix =
    showTimeGroup && timeGroup
      ? getTextWithIcon(` · ${shiftTimeGroupNames[timeGroup]}`, csTimeGroupIconMap[timeGroup])
      : null;
  const withSuffix = (text: string | null): ReactNode => (suffix ? getTextWithIcon(text, suffix) : text);

  switch (shiftData.shiftType) {
    case CSShiftType.OnCall: {
      const onCallText = withSuffix(`${baseText}${isExtra ? ' (Extra)' : ''}`);
      return isTravel ? getTextWithIcon(onCallText, <MdOutlineLuggage size={16} />) : onCallText;
    }
    case CSShiftType.NoCall: {
      const label = withSuffix(isExtra ? 'Floating Holiday' : baseText);
      if (isExtra) return label;
      return fixationType === ShiftFixationType.Required ? getTextWithIcon(label, <MdBlock size={16} />) : label;
    }
    case CSShiftType.TravelDay:
      return getTextWithIcon(withSuffix(baseText), <MdOutlineFlightTakeoff size={16} />);
    default:
      return baseText ? withSuffix(baseText) : baseText;
  }
};

export const getCSShiftTypeSecondaryText = (shiftData: CSShift) => {
  const organNames = shiftData.organs?.join(', ') ?? null;

  switch (shiftData.shiftType) {
    case CSShiftType.OnCall:
    case CSShiftType.PhoneSupport:
      return organNames;
    default:
      return null;
  }
};

export const getShiftOptionIconColor = (shiftOption?: CSShiftOptionType) => {
  switch (shiftOption) {
    case CSShiftOptionType.ExtraCall:
      return 'text-success-30';
    case CSShiftOptionType.OnCall:
      return 'text-success-40';
    case CSShiftOptionType.PhoneSupport:
      return 'text-indigo-30';
    case CSShiftOptionType.Sick:
      return 'text-warning-40';
    case CSShiftOptionType.Vacation:
      return 'text-warning-40/20';
    case CSShiftOptionType.AtHub:
      return 'text-info-40';
    case CSShiftOptionType.Unavailable:
      return 'text-danger-30';
    case CSShiftOptionType.NoCall:
    case CSShiftOptionType.BlockedNoCall:
    case CSShiftOptionType.WeekendOff:
    case CSShiftOptionType.TravelDay:
    case CSShiftOptionType.FloatingHoliday:
      return 'text-base-30';
    case CSShiftOptionType.OfficeDay:
    default:
      return 'text-base-5';
  }
};

export const csDayConflictHighlightGetter = <V extends BaseViolationItem>(item: V) => {
  return {
    hubId: item.id,
    day: item.day ? toLocalDateFromApiDateKey(item.day) : undefined,
  };
};

const CS_WD_SHIFT_TYPES = new Set([CSShiftType.Unavailable, CSShiftType.Sick, CSShiftType.Vacation]);

export const getCSShowHrIcon = (shiftData: CSShift): boolean =>
  !!shiftData.isHrShift && CS_WD_SHIFT_TYPES.has(shiftData.shiftType);
